import { useId } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, ButtonBase } from '@mui/material';

// Booking-destination chooser.
//
// Deliberately mirrors the house V2ChoiceGroup pattern
// (V2BookingDetailsStep.jsx:71-99, :141-162) rather than inventing a second
// look: the CRM booking step renders Method / Type / Contact with exactly these
// pills, and this control sits directly above them. Same label style, same
// radius, same brand-green selected state, same aria-pressed contract.
//
// Hex values are mirrored from choiceChipSx/SUB_LABEL_V2 verbatim (the repo
// convention for these V2 pills) so the two groups can't drift apart visually.
// Extracting them into theme/palette.js is worth doing, but it must move
// V2ChoiceGroup at the same time or the pills stop matching.
const GROUP_LABEL_SX = {
    fontSize: '12px', fontWeight: 600, color: '#3c3c43',
    letterSpacing: '-0.005em', mb: 0.5,
};

const modeChipSx = (isSelected, disabled) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    px: 1.5, py: 0.6,
    borderRadius: '999px',
    fontSize: '12px', fontWeight: 600,
    letterSpacing: '-0.005em',
    border: '1px solid',
    transition: 'all 150ms ease',
    // App.css:157 sets `button { text-transform: capitalize !important }` globally.
    // The previous version of this control used clickable <Box> (divs), so it
    // escaped that rule; real <button>s for keyboard access do not, and "CRM only"
    // rendered as "CRM Only". Class specificity + !important beats the element
    // selector. The global rule is worth retiring, but not from this component —
    // it would restyle every button in the app.
    textTransform: 'none !important',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    ...(isSelected
        ? { color: '#00AB55', borderColor: '#00AB55', bgcolor: 'rgba(0,171,85,0.08)' }
        : {
            color: '#3c3c43', borderColor: 'rgba(0,0,0,0.12)', bgcolor: 'transparent',
            ...(disabled ? {} : { '&:hover': { borderColor: '#00AB55', bgcolor: 'rgba(0,171,85,0.06)' } }),
        }),
    // Keyboard affordance the previous clickable-Box version had no way to show.
    '&.Mui-focusVisible': {
        outline: '2px solid #00AB55',
        outlineOffset: '2px',
    },
});

const MODES = [
    // `dentally` is the practice's own diary product — staff-facing language, not
    // internal API terminology. "CRM only" spells out the consequence of the
    // other branch: the practice diary does NOT receive this appointment.
    { dentally: true, label: 'Dentally', hint: 'Books into the practice diary and the CRM' },
    { dentally: false, label: 'CRM only', hint: 'Books into the CRM only' },
];

export default function BookingModeToggle({ useDentally, onChange, disabled = false, label = 'Book into', sx }) {
    // Unique per instance — OverView/CreateLead/NewLead each mount this in two
    // mutually exclusive branches, and a fixed id would collide if that ever
    // stops being mutually exclusive.
    const labelId = useId();

    // Default spacing is the dialog-chrome inset this control shipped with
    // (injected between DialogTitle and DialogContent). Call sites that render it
    // inline alongside other section labels pass sx to drop the inset so the
    // group label lines up with its siblings.
    return (
        <Box sx={{ px: 3, pt: 2, mb: 1, ...sx }}>
            <Typography id={labelId} sx={GROUP_LABEL_SX}>
                {label}
            </Typography>
            <Box
                role="group"
                aria-labelledby={labelId}
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
            >
                {MODES.map((mode) => {
                    const isSelected = mode.dentally === Boolean(useDentally);
                    return (
                        <ButtonBase
                            key={mode.label}
                            onClick={() => { if (!disabled) onChange(mode.dentally); }}
                            disabled={disabled}
                            aria-pressed={isSelected}
                            aria-label={`${mode.label} — ${mode.hint}`}
                            sx={modeChipSx(isSelected, disabled)}
                        >
                            {mode.label}
                        </ButtonBase>
                    );
                })}
            </Box>
        </Box>
    );
}

BookingModeToggle.propTypes = {
    useDentally: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    sx: PropTypes.object,
};

BookingModeToggle.defaultProps = {
    disabled: false,
    label: 'Book into',
    sx: undefined,
};
